import { Box, Center, Fade, Flex, HStack, SlideFade, Spacer, Spinner, Stack, Text } from '@chakra-ui/react';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';

import { Button } from '../../../design-system/button';
import { useMenuSize } from '../../../design-system/hooks';
import { ArrowLeftOutline, XOutline } from '../../../design-system/icon';
import { Heading } from '../../../design-system/typography';
import { rem } from '../../../design-system/utils';
import { useLocalStorage } from '../../../lib/storage';
import { useCurrentCompany } from '../../../lib/who-am-i';
import { DimensionKey } from '../../../queries/recognition-analysis';
import { Bizy, useBizyFlightAnchor, useIsBizyFlightAnchorHidden } from '../../bizy';
import { Reveal } from '../../display/reveal';
import { ErrorBoundary } from '../../error/error-boundary';
import { getDraftAssistBizyState } from './draft-assist-bizy-state';
import { DraftAssistProp, useHintCopyKey } from './hooks/use-draft-assist';
import {
	CompanyValues,
	Dimension,
	DimensionBox,
	DimensionContext,
	DimensionExamples,
	ScoreboxFeedback,
	useDimensionAccordion,
	useScoreboxState,
} from './scorebox-components';

type DraftAssistPopoutProps = {
	dimensions: Dimension[];
	isLoading: boolean;
	isScoring?: boolean;
	text: string;
	draftAssist?: DraftAssistProp;
	hasUnmetDimension?: boolean;
	hintDimensionKey?: string | null;
	recipientCount?: number;
	onOpenChange?: (isOpen: boolean) => void;
	onDimensionExpanded?: (key: string | null) => void;
};

export const DraftAssistPopout: FC<DraftAssistPopoutProps> = ({
	dimensions,
	isLoading,
	isScoring,
	text,
	draftAssist,
	hasUnmetDimension,
	hintDimensionKey,
	recipientCount,
	onOpenChange,
	onDimensionExpanded,
}) => {
	const { t } = useTranslation('recognition', { keyPrefix: 'gratitudeGauge' });
	// Always starts closed so the LLM only fires on explicit user intent.
	// Session state resets on each modal open — no localStorage restore.
	const [isOpen, setIsOpen] = useState(false);
	const ref = useMenuSize();
	const { track } = useAnalytics();
	const state = useScoreboxState();
	const hasCoreValues = useCurrentCompany().core_values.length > 0;
	const derivedIndex = draftAssist?.dimensionKey != null ? dimensions.findIndex((d) => d.key === draftAssist.dimensionKey) : null;
	const overrideIndex = derivedIndex !== -1 ? derivedIndex : null;
	const { register } = useDimensionAccordion(overrideIndex);

	const [hasSeenIntro, setHasSeenIntro] = useLocalStorage('bizy-intro-seen', false);
	// Permanently latch hasSeenIntro when scoring first produces a hint, so the intro doesn't
	// reappear during transient re-scoring cycles where hasUnmetDimension briefly flips to false.
	// Clicking Bizy also permanently dismisses it (see handleToggle).
	useEffect(() => {
		if (hasUnmetDimension && !hasSeenIntro) {
			setHasSeenIntro(true);
		}
	}, [hasUnmetDimension, hasSeenIntro, setHasSeenIntro]);
	const showIntro = !hasSeenIntro && !hasUnmetDimension;

	const hintCopyKey = useHintCopyKey(hintDimensionKey, recipientCount);
	const gaugeBizyAnchorRef = useBizyFlightAnchor('giveBoxGauge');
	const isGaugeBizyHidden = useIsBizyFlightAnchorHidden('giveBoxGauge');

	const handleToggle = () => {
		if (!hasSeenIntro) setHasSeenIntro(true);

		const nextIsOpen = !isOpen;

		if (nextIsOpen) {
			track(AnalyticsEvent.GratitudeGauge.Opened, { type: 'popout' });
		} else {
			track(AnalyticsEvent.GratitudeGauge.Closed, { type: 'popout' });
		}

		setIsOpen(nextIsOpen);
		onOpenChange?.(nextIsOpen);
	};

	const bizyState = useMemo(() => getDraftAssistBizyState({ isLoading, isScoring, isOpen }), [isLoading, isScoring, isOpen]);

	return (
		<ErrorBoundary>
			<Flex direction="row" position="absolute" top={0} left="calc(100% + 24px)" pointerEvents="none">
				<Box position="absolute" top={0} right="calc(100% - 12px)">
					<Box aria-label={t('buttonLabel')} as="button" cursor="pointer" pointerEvents="auto" onClick={handleToggle}>
						<Box ref={gaugeBizyAnchorRef} visibility={isGaugeBizyHidden ? 'hidden' : 'visible'}>
							<Bizy state={bizyState} size="md" />
						</Box>
					</Box>
					<Fade in={showIntro || (!showIntro && !!hasUnmetDimension && !isOpen)} unmountOnExit>
						<Box
							as="button"
							onClick={handleToggle}
							cursor="pointer"
							pointerEvents="auto"
							position="absolute"
							bottom={'calc(100% - 42px)'}
							left={'calc(100% - 18px)'}
							w="200px"
							bg="bg.surface"
							border="1px solid"
							borderColor="border.soft"
							borderRadius="12px 12px 12px 4px"
							boxShadow="0 3px 3px 0 rgba(0,0,0,0.08)"
							backdropFilter="blur(2px)"
							px={3}
							py={2}
							fontFamily="Plus Jakarta Sans"
							textAlign="left"
						>
							{showIntro ? (
								<Text fontSize="sm" fontWeight="medium" textAlign="left">
									{t('bizyIntro')}
								</Text>
							) : (
								<Stack gap="3px" align="flex-start">
									<Text fontSize="sm" fontWeight="semibold" textAlign="left">
										{t(hintCopyKey)}
									</Text>
									<Text color="text.softest" fontSize="xs" textAlign="left">
										{t('draftAssistHints.tapToSeeIdeas')}
									</Text>
								</Stack>
							)}
						</Box>
					</Fade>
				</Box>

				<SlideFade in={isOpen} style={{ pointerEvents: isOpen ? 'auto' : 'none' }}>
					<DimensionContext.Provider value={{ stage: state.stage }}>
						<Box
							w={rem(322)}
							bg="bg.minipage"
							backdropFilter="blur(44px)"
							border="1px solid"
							borderColor="border.default"
							borderRadius={rem(24)}
							overflow="hidden"
							overflowY="auto"
							ref={ref}
							position="absolute"
							top="calc(100% - 56px)"
						>
							<Flex
								transform={state.isViewing ? `translateX(calc(-100% - ${rem(16)}))` : 'translateX(0)'}
								transition="transform 0.3s cubic-bezier(0.68, -0.3, 0.265, 1.25)"
								columnGap={4}
								w="full"
							>
								<Box w="full" flexShrink={0}>
									<Reveal if={!state.isViewing || state.isTransitioning}>
										<HStack columnGap={2} px={4} py={3} alignItems="center">
											<Heading color="text.default">{t('bizyTitle')}</Heading>
											<Spacer />
											<Fade in={isLoading}>
												<Spinner size="sm" color="purple.300" />
											</Fade>
											<Center
												as="button"
												onClick={handleToggle}
												color="text.subdued"
												borderRadius="full"
												bg="blackAlpha.100"
												boxSize={8}
												cursor="pointer"
												flexShrink={0}
											>
												<XOutline size="xs" />
											</Center>
										</HStack>

										<Stack rowGap={2} px={4} pb={4} pt={0}>
											{dimensions.map((dimension, index) => {
												const registered = register(index);
												const isMatchingDimension = draftAssist?.dimensionKey === dimension.key;
												const isLoadingForDimension =
													!!draftAssist?.isLoading && (draftAssist.loadingDimensionKey === dimension.key || draftAssist.dimensionKey === dimension.key);
												return (
													<DimensionBox
														dimension={dimension}
														key={dimension.key}
														text={text}
														unmetBgColor="bg.surface"
														onClick={dimension.key === DimensionKey.ValuesAligned ? () => state.onViewDimension(dimension) : undefined}
														isOpen={registered.isOpen}
														onToggle={() => {
															const nextIsOpen = !registered.isOpen;
															registered.onToggle();
															if (dimension.key !== DimensionKey.ValuesAligned) {
																onDimensionExpanded?.(nextIsOpen ? dimension.key : null);
															}
														}}
														isLoadingSuggestion={isLoadingForDimension}
														suggestion={isMatchingDimension ? draftAssist.suggestion : undefined}
														onAddSuggestion={isMatchingDimension && draftAssist?.onAdd ? (t: string) => draftAssist.onAdd?.(t) : undefined}
														onRegenerateSuggestion={isMatchingDimension ? draftAssist?.onRegenerate : undefined}
													/>
												);
											})}
										</Stack>

										<ScoreboxFeedback dimensions={dimensions} px={4} pb={4} text={text} />
									</Reveal>
								</Box>

								<Box w="full" flexShrink={0}>
									<Reveal if={state.isViewing || state.isTransitioning}>
										<HStack columnGap={1} px={4} py={3}>
											<Box as="button" px={2} onClick={state.onCloseDimension} color="text.default">
												<ArrowLeftOutline size="xs" />
											</Box>
											<Heading color="text.default">{t('exampleTitle', { context: state.dimension?.key })}</Heading>
										</HStack>

										<Stack rowGap={2} p={4}>
											{state.dimension && (state.dimension.key !== DimensionKey.ValuesAligned || !hasCoreValues) && (
												<DimensionExamples dimension={state.dimension} text={text} />
											)}
											{state.dimension && state.dimension.key === DimensionKey.ValuesAligned && hasCoreValues && <CompanyValues />}

											<Button colorScheme="secondary" variant="alpha" mt={4} onClick={state.onCloseDimension}>
												{t('back')}
											</Button>
										</Stack>
									</Reveal>
								</Box>
							</Flex>
						</Box>
					</DimensionContext.Provider>
				</SlideFade>
			</Flex>
		</ErrorBoundary>
	);
};
